import type { ClickPoint, InteractionEvent, MarkerEvent } from "./types";

function now(): number {
  return Date.now();
}

function viewport(): { w: number; h: number } {
  return { w: window.innerWidth, h: window.innerHeight };
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * Robuster Klick-Punkt (Vorbild: comments.ts anchorFor): Pixel-Koordinaten UND
 * Element-Anker (rrweb-Mirror-ID + Offset in der BBox). Damit lassen sich
 * Heatmaps element-verankert re-projizieren, wenn sich das Layout aendert.
 */
function anchorPoint(ev: MouseEvent, getNodeId?: (n: Node | null) => number): ClickPoint {
  const vw = Math.max(1, window.innerWidth);
  const vh = Math.max(1, window.innerHeight);
  const point: ClickPoint = {
    vx: clamp01(ev.clientX / vw),
    vy: clamp01(ev.clientY / vh),
    px: Math.round(ev.clientX + window.scrollX),
    py: Math.round(ev.clientY + window.scrollY),
  };
  const el = ev.target instanceof Element ? ev.target : null;
  if (el) {
    const r = el.getBoundingClientRect();
    if (r.width > 0) point.fx = clamp01((ev.clientX - r.left) / r.width);
    if (r.height > 0) point.fy = clamp01((ev.clientY - r.top) / r.height);
    const id = getNodeId ? getNodeId(el) : -1;
    if (id > 0) point.nodeId = id;
  }
  return point;
}

/** Lesbarer Selektor/Label für ein Element — bevorzugt `data-ul-name`. */
export function selectorFor(node: EventTarget | null): string {
  let el = node as Element | null;
  if (!el || !(el instanceof Element)) return "";

  const named = el.closest("[data-ul-name]");
  if (named) {
    const v = named.getAttribute("data-ul-name");
    if (v) return v;
  }

  // Bis zu einem klickbaren Vorfahren hochgehen (Button/Link).
  const clickable = el.closest("button, a, [role=button], input, label, summary");
  if (clickable) el = clickable;

  const parts: string[] = [];
  if (el.id) parts.push(`#${el.id}`);
  parts.push(el.tagName.toLowerCase());
  const cls = (el.getAttribute("class") || "").trim().split(/\s+/).filter(Boolean)[0];
  if (cls) parts.push(`.${cls}`);

  let label = parts.join("");
  const text = (el.textContent || "").trim().replace(/\s+/g, " ");
  if (text) label += ` "${text.slice(0, 40)}"`;
  return label;
}

interface Handlers {
  onInteraction: (e: InteractionEvent) => void;
  onMarker: (e: MarkerEvent) => void;
  /** rrweb-Mirror-ID eines Knotens (fuer element-verankerte Klick-Koordinaten). */
  getNodeId?: (node: Node | null) => number;
}

/**
 * Hängt High-Level-Listener an (US1.2) und erkennt Frust-/Fehler-Marker (US1.4).
 * Gibt eine Cleanup-Funktion zurück.
 */
export function attachInteractions({ onInteraction, onMarker, getNodeId }: Handlers): () => void {
  // --- Klicks + Rage-Click-Heuristik ------------------------------------
  let lastTarget = "";
  let clickTimes: number[] = [];

  const onClick = (ev: MouseEvent) => {
    const target = selectorFor(ev.target);
    const t = now();
    const point = anchorPoint(ev, getNodeId);
    onInteraction({ t, type: "click", target, viewport: viewport(), point });

    // Rage-Click: >= 3 Klicks auf dasselbe Ziel innerhalb 1 s.
    if (target === lastTarget) {
      clickTimes.push(t);
      clickTimes = clickTimes.filter((x) => t - x < 1000);
      if (clickTimes.length >= 3) {
        onMarker({ t, type: "rage_click", payload: { target, point } });
        clickTimes = [];
      }
    } else {
      lastTarget = target;
      clickTimes = [t];
    }
  };

  // --- Scroll (throttled) -----------------------------------------------
  let scrollTimer: ReturnType<typeof setTimeout> | undefined;
  const onScroll = () => {
    if (scrollTimer) return;
    scrollTimer = setTimeout(() => {
      scrollTimer = undefined;
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - window.innerHeight);
      const pct = Math.round((window.scrollY / max) * 100);
      onInteraction({ t: now(), type: "scroll", data: { scrollY: window.scrollY, pct } });
    }, 500);
  };

  // --- Fenster-Resize (debounced) — Browser-Dimension über die Zeit -----
  let resizeTimer: ReturnType<typeof setTimeout> | undefined;
  const onResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resizeTimer = undefined;
      onInteraction({
        t: now(),
        type: "resize",
        data: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
      });
    }, 300);
  };

  // --- Eingaben (nur Meta, KEIN Wert) -----------------------------------
  const onInput = (ev: Event) => {
    onInteraction({ t: now(), type: "input", target: selectorFor(ev.target) });
  };

  // --- SPA-Routing (History API + hashchange) ---------------------------
  let currentHref = location.href;
  const emitRoute = () => {
    const to = location.href;
    if (to === currentHref) return;
    const from = currentHref;
    currentHref = to;
    onInteraction({ t: now(), type: "route_change", data: { from, to } });
  };
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (this: History, ...args: Parameters<History["pushState"]>) {
    const r = origPush.apply(this, args);
    emitRoute();
    return r;
  };
  history.replaceState = function (this: History, ...args: Parameters<History["replaceState"]>) {
    const r = origReplace.apply(this, args);
    emitRoute();
    return r;
  };

  // --- JS-Fehler --------------------------------------------------------
  const onError = (ev: ErrorEvent) => {
    onMarker({
      t: now(),
      type: "js_error",
      payload: { message: ev.message, source: ev.filename, line: ev.lineno },
    });
  };
  const onRejection = (ev: PromiseRejectionEvent) => {
    onMarker({ t: now(), type: "js_error", payload: { message: String(ev.reason) } });
  };

  // --- Tab-/Fenster-Inaktivität (visibilitychange) ----------------------
  // Misst, wann der Proband den Tab verlässt/minimiert (abgelenkt/weg) und wie
  // lange. Auf der Zeitachse als Marker; beim Zurückkommen mit Dauer (awayMs).
  let hiddenSince = 0;
  const onVisibility = () => {
    if (document.hidden) {
      hiddenSince = now();
      onMarker({ t: now(), type: "tab_hidden" });
    } else {
      const awayMs = hiddenSince ? now() - hiddenSince : 0;
      hiddenSince = 0;
      onMarker({ t: now(), type: "tab_visible", payload: { awayMs } });
    }
  };

  document.addEventListener("click", onClick, true);
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize, { passive: true });
  document.addEventListener("change", onInput, true);
  window.addEventListener("popstate", emitRoute);
  window.addEventListener("hashchange", emitRoute);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    document.removeEventListener("click", onClick, true);
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onResize);
    document.removeEventListener("change", onInput, true);
    window.removeEventListener("popstate", emitRoute);
    window.removeEventListener("hashchange", emitRoute);
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    document.removeEventListener("visibilitychange", onVisibility);
    history.pushState = origPush;
    history.replaceState = origReplace;
    if (scrollTimer) clearTimeout(scrollTimer);
    if (resizeTimer) clearTimeout(resizeTimer);
  };
}
